'use client';

import { Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import type {
  GetSeedingMeProposalServiceResponse,
  SeedingProposalStatus,
} from '@musinsa/services-curator';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getPaths } from '../../common';
import { useCuratorSnackbar, useNavigate } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';
import { useSeedingProposalRejectMutation } from '../hooks/useSeedingProposalMutation';
import { isSeedingContentDeadlineExpired } from '../utils/isSeedingContentDeadlineExpired';
import { SeedingContentExpiredGroupHeader } from './SeedingContentExpiredGroupHeader';
import { SeedingProposalCard } from './SeedingProposalCard';
import { ProposeRejectReasonSheet } from './seedingDetail/interactionSheets';

const PROPOSAL_EMPTY_TEXT: Record<SeedingProposalStatus, string> = {
  PENDING: '제안 받은 캠페인이 없습니다.',
  CONFIRMED: '수락한 캠페인이 없습니다.',
  COMPLETED: '완료된 캠페인이 없습니다.',
  REJECTED: '거절한 캠페인이 없습니다.',
};

interface Props {
  status: SeedingProposalStatus;
}

const PAGE_SIZE = 20;

export const SeedingProposalList = ({ status }: Props) => {
  const queryParams = useMemo(() => ({ searchStatus: status, size: PAGE_SIZE }), [status]);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      ...curatorQueries.campaigns.seedingMeProposal(queryParams),
      initialPageParam: 1,
      getNextPageParam: (lastPage: GetSeedingMeProposalServiceResponse) =>
        lastPage.pagination.nextPage ?? undefined,
    });

  const snackbar = useCuratorSnackbar();
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const paths = getPaths(service);
  const [rejectTargetId, setRejectTargetId] = useState<number | null>(null);

  const rejectMutation = useSeedingProposalRejectMutation({
    onSuccess: () => {
      snackbar.success('캠페인이 거절되었습니다.');
      setRejectTargetId(null);
    },
    onError: () => {
      snackbar.error('캠페인 제안 거절에 실패했습니다.');
    },
  });
  const isActionPending = rejectMutation.isPending;

  const { data: rejectionReasonsData } = useQuery({
    ...curatorQueries.campaigns.seedingProposalRejectionReasons(),
    enabled: rejectTargetId !== null,
    staleTime: 60 * 60 * 1000,
  });
  const reasons = rejectionReasonsData?.reasons ?? [];

  const items = useMemo(
    () => data?.pages.flatMap((page: GetSeedingMeProposalServiceResponse) => page.list) ?? [],
    [data],
  );

  // 완료 탭 한정: 정상 완료 / 콘텐츠 제출 기한 경과(미제출) 2그룹 분할. 그 외 탭은 평면 렌더.
  // (QAT-39690 §3 / CMQA-7189 — 제안형 동형)
  const isCompletedTab = status === 'COMPLETED';
  const normalItems = useMemo(
    () => (isCompletedTab ? items.filter((it) => !isSeedingContentDeadlineExpired(it)) : items),
    [isCompletedTab, items],
  );
  const expiredItems = useMemo(
    () => (isCompletedTab ? items.filter(isSeedingContentDeadlineExpired) : []),
    [isCompletedTab, items],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '600px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-[16px]! py-[40px]!">
        <Typography variant="body14pxReg" colorScheme="gray-500">
          불러오는 중…
        </Typography>
      </div>
    );
  }

  if (isError && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-[16px]! py-[40px]!">
        <Typography variant="body14pxReg" colorScheme="gray-500">
          캠페인 목록을 불러오지 못했어요.
        </Typography>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-[12px]! px-[16px]! py-[40px]!">
        <div className="flex flex-col items-center gap-[2px]! w-full">
          <Typography size="14" weight="medium">
            {PROPOSAL_EMPTY_TEXT[status]}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    // 하단 72px 여백 — PLP 하단 FAB/safe-area 클리어런스. (피드백 항목18)
    <div className="pt-[12px]! pb-[72px]!">
      {normalItems.map((item, index) => (
        <SeedingProposalCard
          key={item.participationId}
          item={item}
          index={index}
          isActionPending={isActionPending}
          isLast={index === normalItems.length - 1}
          onAccept={() => {
            // SID-3486: 통일 상세(수락 전 검토 화면)로 이동. 수락 mutation 은 상세의 폼 제출 시 호출.
            navigate(paths.campaignSeedingDetail({ campaignId: item.campaignId }));
          }}
          onReject={(id) => setRejectTargetId(id)}
        />
      ))}
      {expiredItems.length > 0 && <SeedingContentExpiredGroupHeader />}
      {expiredItems.map((item, index) => (
        // 완료 탭 기한경과 그룹 — searchStatus 'COMPLETED' 라 카드 내부에서 액션/divider 미노출.
        <SeedingProposalCard
          key={item.participationId}
          item={item}
          index={normalItems.length + index}
          isLast
        />
      ))}
      <div ref={sentinelRef} className="h-[1px]" />
      <ProposeRejectReasonSheet
        open={rejectTargetId !== null}
        onClose={() => setRejectTargetId(null)}
        reasons={reasons}
        submitting={rejectMutation.isPending}
        onSubmit={(rejectionReasonCodes) => {
          if (rejectTargetId === null) return;
          rejectMutation.mutate({ participationId: rejectTargetId, rejectionReasonCodes });
        }}
      />
    </div>
  );
};
